import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  api,
  type ApiError,
  type GetFlowResult,
  type GetFlowRunResult,
  type ListFlowRunsResult,
  type ListPalettesResult,
} from '@whinerp/client';
import {
  Button,
  EmptyState,
  ErrorState,
  FlowCanvas,
  Num,
  PageHeading,
  Panel,
  SectionLabel,
  Skeleton,
  useToast,
} from '@whinerp/ui';
import type { FlowGraph, Palette } from '@whinerp/contracts/flow';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';
import { flowCanvasLabels } from './flow-labels.js';

/**
 * SC-22 Automation run log.
 *
 * The path a run took, drawn on the canvas it took it through. A run log that
 * lists steps beside a static diagram asks somebody to hold the route in their
 * head, and the reason they opened the log is that they cannot.
 *
 * The branch not taken is shown as not taken rather than left unmarked,
 * because an unmarked node reads the same as one that ran and did nothing -
 * and the difference between those two is the entire question.
 */

function RunLogScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const toast = useToast();
  const params = useParams();
  const flowId = params['id'] ?? '';
  const [selectedRun, setSelectedRun] = useState<string | null>(null);

  const flow = useQuery<GetFlowResult, ApiError>({
    queryKey: ['flow', flowId],
    queryFn: () => api.getFlow({ path: { flowId } }),
  });

  const palettes = useQuery<ListPalettesResult, ApiError>({
    queryKey: ['palettes'],
    queryFn: () => api.listPalettes({}),
  });

  const runs = useQuery<ListFlowRunsResult, ApiError>({
    queryKey: ['flow-runs', flowId],
    queryFn: () => api.listFlowRuns({ path: { flowId }, query: {} }),
  });

  const runId = selectedRun ?? runs.data?.runs[0]?.runId ?? null;

  const run = useQuery<GetFlowRunResult, ApiError>({
    queryKey: ['flow-run', runId],
    queryFn: () => api.getFlowRun({ path: { runId: runId ?? '' } }),
    enabled: Boolean(runId),
  });

  const replay = useMutation({
    mutationFn: () =>
      api.replayFlowRun({ path: { runId: runId ?? '' }, body: { record: { amount: '5000.00' } } }),
    onSuccess: (result) => {
      // A new run. The original survives the attempt to understand it.
      void runs.refetch();
      setSelectedRun(result.runId);
      toast.show({ tone: 'success', title: t('flow.run.replay'), body: result.outcome });
    },
    onError: (error: ApiError) =>
      // A failed replay reports the reason and leaves the original alone.
      toast.show({ tone: 'error', title: t('state.error'), body: error.message }),
  });

  if (flow.isError) return <RunLogError error={flow.error} retry={() => void flow.refetch()} />;
  if (flow.isPending) return <RunLogLoading />;

  const palette = (palettes.data?.palettes.find((entry) => entry.key === flow.data.palette) ??
    null) as Palette | null;

  return (
    <div data-component="RunLog" className="flex w-full flex-col gap-4">
      <PageHeading>{flow.data.name}</PageHeading>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[40%_60%]">
        <Panel data-region="runs">
          <div className="flex flex-col gap-2">
            <SectionLabel>{t('flow.runs')}</SectionLabel>
            {runs.isPending ? (
              <Skeleton className="h-block-md w-full" />
            ) : (runs.data?.runs ?? []).length === 0 ? (
              <p className="text-body text-ink-meta">{t('flow.run.empty')}</p>
            ) : (
              <ul className="flex flex-col">
                {(runs.data?.runs ?? []).map((entry) => (
                  <li key={entry.runId} className="border-t border-t-rule first:border-t-0">
                    <button
                      type="button"
                      data-run={entry.runId}
                      aria-current={entry.runId === runId ? 'true' : undefined}
                      onClick={() => setSelectedRun(entry.runId)}
                      className={
                        entry.runId === runId
                          ? 'flex w-full flex-col gap-0.5 bg-bronze-tint px-2 py-2 text-start'
                          : 'flex w-full flex-col gap-0.5 px-2 py-2 text-start hover:bg-sunken'
                      }
                    >
                      <span className="flex items-center gap-2">
                        <span
                          data-run-status={entry.status}
                          className={
                            entry.status === 'failed'
                              ? 'inline-flex h-chip items-center rounded-chip bg-danger-tint px-2 text-meta text-danger-text'
                              : entry.status === 'waiting'
                                ? 'inline-flex h-chip items-center rounded-chip bg-warning-tint px-2 text-meta text-warning-text'
                                : 'inline-flex h-chip items-center rounded-chip bg-sunken px-2 text-meta text-ink-600'
                          }
                        >
                          {entry.status}
                        </span>
                        <Num className="text-meta text-ink-meta">
                          {formatDateTime(entry.startedAt, format)}
                        </Num>
                      </span>
                      {/* A paused run says until when. "Waiting" on its own
                          is the same sentence as a stuck run, and the two
                          need different reactions. */}
                      {entry.status === 'waiting' && entry.resumeAt ? (
                        <span data-run-resume-at={entry.runId} className="truncate text-dense text-ink">
                          {t('flow.run.waitingUntil', { values: { when: formatDateTime(entry.resumeAt, format) } })}
                        </span>
                      ) : (
                        <span className="truncate text-dense text-ink">{entry.outcome ?? ''}</span>
                      )}
                      {/* Why it stopped early, when something took it out:
                          "left the journey" and "reached the end" are
                          different facts about a customer. */}
                      {entry.exitReason ? (
                        <span data-run-exit={entry.runId} className="truncate text-meta text-ink-meta">
                          {t('flow.run.left', { values: { reason: entry.exitReason } })}
                        </span>
                      ) : null}
                      <Num className="text-meta text-ink-meta">
                        {t('flow.run.steps', {
                          values: { count: formatCount(entry.steps, format) },
                        })}
                      </Num>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Panel>

        <Panel data-region="detail">
          <div className="flex flex-col gap-2">
            <span className="flex flex-wrap items-center gap-3">
              <SectionLabel>{t('flow.run.outcome')}</SectionLabel>
              <span data-run-outcome className="text-dense text-ink">
                {run.data?.outcome ?? ''}
              </span>
              {runId ? (
                <Button
                  intent="secondary"
                  data-replay
                  disabled={replay.isPending}
                  onClick={() => replay.mutate()}
                  className="ms-auto"
                >
                  {t('flow.run.replay')}
                </Button>
              ) : null}
            </span>

            {/* Nothing has run. Said above the canvas rather than instead of
                it: the flow is still worth looking at, and a skeleton that
                never resolves reads as a screen that is still loading. */}
            {runId === null && !runs.isPending ? <RunLogEmpty /> : null}

            {palette ? (
              // The path, on the canvas it ran through. No palette here: a run
              // log is not an editor, and offering one invites changing the
              // thing being debugged.
              <FlowCanvas
                graph={flow.data.graph as FlowGraph}
                palette={palette}
                labels={flowCanvasLabels(t)}
                {...(run.data ? { executed: run.data.steps } : {})}
              />
            ) : (
              <Skeleton className="h-block-xl w-full" />
            )}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function RunLogEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="ClockCounterClockwise"
      title={t('flow.run.empty')}
      body={t('flow.empty.body')}
      primary={{ label: t('empty.action.runIt'), icon: 'Play', href: '/work' }}
    />
  );
}

function RunLogLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-4" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-block-lg" />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Skeleton className="h-block-xl w-full" />
        <Skeleton className="h-block-xl w-full" />
      </div>
    </div>
  );
}

function RunLogError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="The run log could not be loaded"
      body={error instanceof Error ? error.message : 'It did not load.'}
      onRetry={retry}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-22',
  route: '/automations/:id/runs',
  surface: 'page',
  title: 'Automation run log',
  // F-095. The run log is the debugger, not the builder.
  features: ['F-095'],
  entitlement: 'F-068',
  reachedBy: [{ kind: 'screen', from: 'SC-21', how: 'the run log link on the builder' }],
  components: ['FlowCanvas'],
  elements: ['RunLog'],
  sampleRecord: { objectKey: 'flow', param: 'id' },
  states: {
    empty: RunLogEmpty,
    loading: RunLogLoading,
    error: RunLogError,
  },
  component: RunLogScreen,
});

export default screen;
